import {
  Component,
  DestroyRef,
  HostBinding,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavItem } from './nav-item';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { resolveActiveSessionCompany } from '@core/tenant/tenant-company.utils';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { isNavRouteActive } from '@core/shell/nav-route-active.utils';
import { NavService } from '../../../../../services/nav.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmOpenTabDialogComponent,
  ConfirmOpenTabDialogData,
} from '@shared/ui/confirm-open-tab-dialog/confirm-open-tab-dialog.component';
import {
  confirmEnterPosVenta,
  isPosShellEntryRoute,
  openPosRouteInNewTab,
  POS_VENTA_ENTRY_ROUTE,
} from '@shared/ui/confirm-open-tab-dialog/confirm-enter-pos';
import { ToastService } from '@shared/ui/toast/toast.service';
import { PosOrdersHubNavBadgeService } from '@features/pos/public-api';
import { NavIconPairComponent } from '@shared/ui/nav-icon-pair/nav-icon-pair.component';


@Component({
  selector: 'app-nav-item',
  imports: [
    TranslateModule,
    TablerIconsModule,
    MaterialModule,
    CommonModule,
    NavIconPairComponent,
  ],
  templateUrl: './nav-item.component.html',
  styleUrls: [],
  animations: [
    trigger('indicatorRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4,0.0,0.2,1)')
      ),
    ]),
  ],
  host: {
    class: 'nav-item-host',
    style: 'display:block;width:100%;',
  },
})
export class AppNavItemComponent implements OnChanges, OnInit {
  @Output() toggleMobileLink = new EventEmitter<void>();
  @Output() notify: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() selectedIcon = '';
  expanded = false;
  disabled = false;
  twoLines = false;
  @HostBinding('attr.aria-expanded') ariaExpanded = this.expanded;
  @Input() item: NavItem = {};
  @Input() depth = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly authSession = inject(AuthSessionService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly toast = inject(ToastService);
  private readonly hubBadge = inject(PosOrdersHubNavBadgeService);
  readonly hubPendingCount = this.hubBadge.pendingCount;

  constructor(
    public navService: NavService,
    public router: Router,
    private readonly ngZone: NgZone
  ) {
    this.depth = 0;
  }

  /**
   * Fallback si MDC/`pointer-events` hace que el clic no llegue al `<button>` interno.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || target.closest('button, a')) {
      return;
    }
    if (this.item.navCap) {
      return;
    }
    if (this.item.children?.length) {
      this.handleParentNavClick(this.item, event);
    } else if (this.item.route?.trim()) {
      this.navigateLeaf(this.item, event);
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncExpandedFromUrl());
  }

  ngOnChanges() {
    this.syncExpandedFromUrl();
  }

  private syncExpandedFromUrl(): void {
    const rawUrl = this.router.url.split('?')[0];
    if (this.item.route && rawUrl) {
      const path = this.item.route.startsWith('/') ? this.item.route : `/${this.item.route}`;
      this.expanded = rawUrl === path || rawUrl.startsWith(`${path}/`);
      this.ariaExpanded = this.expanded;
    }
  }

  /** Ítems con hijos: solo expandir/colapsar (sin navegar). */
  handleParentNavClick(item: NavItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.disabled || !item.children?.length) {
      return;
    }

    this.expanded = !this.expanded;
    this.ariaExpanded = this.expanded;
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (!this.expanded && window.innerWidth < 1024) {
      this.notify.emit();
    }
  }

  /**
   * Navegación explícita (evita conflictos `RouterLink` + `mat-list-item` MDC).
   * Entrada a `/pos/*` usa `enterPos` (modo shell + return URL).
   */
  navigateLeaf(item: NavItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.disabled || !item.route?.trim()) {
      return;
    }

    let url = item.route.startsWith('/') ? item.route : `/${item.route}`;
    if (url === '/product-catalog') {
      const companySlug = this.resolveActiveCompanySlug();
      url = companySlug ? `/${companySlug}/tienda` : '/product-catalog/tienda';
    }
    if (item.openPosInNewTab) {
      confirmEnterPosVenta(this.dialog).subscribe((confirmed) => {
        if (confirmed) {
          openPosRouteInNewTab(POS_VENTA_ENTRY_ROUTE);
          if (window.innerWidth < 1024) {
            this.notify.emit();
          }
        }
      });
      return;
    }
    if (item.openInNewTab) {
      const data: ConfirmOpenTabDialogData = {
        titleKey: 'sidebar.openCatalogConfirmTitle',
        messageKey: 'sidebar.openCatalogConfirmMessage',
        confirmKey: 'sidebar.openCatalogConfirmAccept',
        cancelKey: 'sidebar.openCatalogConfirmCancel',
      };
      this.dialog
        .open(ConfirmOpenTabDialogComponent, {
          width: '420px',
          maxWidth: '90vw',
          data,
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (!confirmed) {
            return;
          }
          const absolute =
            url.startsWith('http://') || url.startsWith('https://')
              ? url
              : `${window.location.origin}${url}`;
          window.open(absolute, '_blank', 'noopener,noreferrer');
          if (window.innerWidth < 1024) {
            this.notify.emit();
          }
        });
      return;
    }

    this.ngZone.run(() => {
      if (isPosShellEntryRoute(url)) {
        const posRoute = url === '/pos' ? POS_VENTA_ENTRY_ROUTE : url;
        this.adminShellMode.enterPos(posRoute);
        if (window.innerWidth < 1024) {
          queueMicrotask(() => this.notify.emit());
        }
        return;
      }

      void this.router.navigateByUrl(url).then((ok) => {
        const landed = this.router.url.split('?')[0];
        const target = url.split('?')[0];
        const onTarget = landed === target || landed.startsWith(`${target}/`);
        // Cancel (canDeactivate false): sin toast — el shell realinea el módulo a la URL.
        if (ok && !onTarget) {
          this.toast.errorTranslated('sidebar.navFailed');
        }
      });
    });

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (window.innerWidth < 1024 && !isPosShellEntryRoute(url)) {
      queueMicrotask(() => this.notify.emit());
    }
  }

  onSubItemSelected(item: NavItem) {
    if (!item.children || !item.children.length) {
      if (this.expanded && window.innerWidth < 1024) {
        this.notify.emit();
      }
    }
  }

  isDirectlyActive(item: NavItem): boolean {
    return isNavRouteActive(this.router, item.route);
  }

  isChildActive(item: NavItem): boolean {
    if (!item.children) return false;
    return item.children.some(
      (child) => this.isDirectlyActive(child) || this.isChildActive(child)
    );
  }

  /** Outline→filled + clase `activeMenu` del panel. */
  isNavIconActive(item: NavItem): boolean {
    return this.isDirectlyActive(item) || this.isChildActive(item);
  }

  isOrdersHubRoute(route: string | undefined): boolean {
    return route === '/pos/pedidos' || route === '/sales/pos/pedidos';
  }

  private resolveActiveCompanySlug(): string | null {
    const activeCompanyId = this.tenantContext.activeCompanyId();
    const companies = this.authSession.user()?.companies ?? [];
    const active = resolveActiveSessionCompany(
      companies,
      activeCompanyId,
      this.tenantContext.operatedCompany()
    );
    const slug = active?.slug?.trim();
    return slug || null;
  }
}
